// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * The `register_surface` a pack form template declares to say "my
 * `transfer_mechanism` field's options are the transfer mechanisms this
 * instrument provides". Declared once, here, so no module spells it.
 */
export const TRANSFER_MECHANISM_REGISTER_SURFACE = 'transfer_mechanism_vocabulary';
/**
 * The `register_surface` a pack declares to say, against the signed text, that
 * its instrument provides NO transfer mechanism. A separate surface rather than
 * an empty option list, for the reason the lawful-basis verified negative is
 * one: an empty list and an absent declaration are the same shape and must not
 * read the same way.
 */
export const TRANSFER_MECHANISM_VERIFIED_NONE_SURFACE = 'transfer_mechanism_declares_none';
/**
 * The two declaration surfaces of this vocabulary, the twin of
 * `LAWFUL_BASIS_DECLARATION_SURFACES`. A template on either decides how every
 * stored mechanism is graded.
 */
export const TRANSFER_MECHANISM_DECLARATION_SURFACES = [
    TRANSFER_MECHANISM_REGISTER_SURFACE,
    TRANSFER_MECHANISM_VERIFIED_NONE_SURFACE,
];
/** The field id whose `options[]` carry the declared mechanisms. */
export const TRANSFER_MECHANISM_VOCABULARY_FIELD = 'transfer_mechanism';
/** Upper bound on a stored mechanism code at every writer's boundary. */
export const TRANSFER_MECHANISM_CODE_MAX_LENGTH = 200;
/** The suffixes the annotation is stored under, beside the code field. */
export const TRANSFER_MECHANISM_ANNOTATION_SUFFIXES = [
    'resolution',
    'declared_by',
    'declared_version',
    'recorded_by',
    'recorded_at',
];
/**
 * The declared option a stored code resolves to, and whether it answered under
 * a superseded spelling. The ONE lookup: the server grades with it at write
 * time and every surface renders a label with it, so the question "which
 * declared mechanism is this stored code?" has one answer in the product.
 */
export function findTransferMechanismOption(options, code) {
    if (typeof code !== 'string' || code.trim().length === 0)
        return null;
    const wanted = code.trim();
    const declared = options.find((option) => option.value === wanted);
    if (declared)
        return { option: declared, via_superseded_value: false };
    const aliased = options.find((option) => option.superseded_values.includes(wanted));
    if (aliased)
        return { option: aliased, via_superseded_value: true };
    return null;
}
/**
 * OD43 / OD43a — has EVERY instrument in the measurement (each instrument of a
 * declaring stack) answered for this surface? False when there is none: an org
 * with no declaring stack has measured nothing (OD38).
 */
export function transferMechanismVocabularyIsComplete(vocabulary) {
    return (vocabulary.instruments.length > 0 &&
        vocabulary.instruments.every((i) => i.state !== 'declaration_not_verified'));
}
/**
 * fix-sprint-20261003-0625 gate E12.5 pass 1 (P2-1) — the two resolutions
 * under which a stored mechanism is one an installed framework declares.
 * Gate E12.5 pass 3 (P3): a gate or a score grades the code LIVE against these
 * (`liveTransferMechanismStanding`, backend); it never trusts the stamp a
 * past write left, because the page does not.
 */
export const DECLARED_TRANSFER_MECHANISM_RESOLUTIONS = [
    'pack_declared',
    'pack_declared_via_superseded_value',
];
/** The instruments that have not answered for this surface, in reader order. */
export function transferMechanismUnverifiedInstruments(vocabulary) {
    return vocabulary.instruments
        .filter((i) => i.state === 'declaration_not_verified')
        .map((i) => i.framework_code);
}
/**
 * How one stored code grades against the vocabulary as it stands now — the
 * rule the write path, the boot migration and every surface share.
 *
 * - No code: `null` (nothing to grade).
 * - Declared, or declared under an older spelling: that resolution, with the
 *   declaring instrument and pack version.
 * - Undeclared, and every instrument of every declaring stack has answered:
 *   `needs_redetermination` (OD43a).
 * - Undeclared, and one of them has not answered — or no stack declares at
 *   all: `null` — an unverified instrument might declare it (OD43).
 *
 * Never maps a code onto a neighbour.
 */
export function gradeTransferMechanism(vocabulary, code) {
    if (typeof code !== 'string' || code.trim().length === 0) {
        return { resolution: null, declared_by: null, declared_version: null };
    }
    const found = findTransferMechanismOption(vocabulary.options, code);
    if (found) {
        return {
            resolution: found.via_superseded_value
                ? 'pack_declared_via_superseded_value'
                : 'pack_declared',
            declared_by: found.option.declared_by_framework_code,
            declared_version: found.option.declared_by_pack_version,
        };
    }
    return {
        resolution: transferMechanismVocabularyIsComplete(vocabulary) ? 'needs_redetermination' : null,
        declared_by: null,
        declared_version: null,
    };
}
export function recordedTransferMechanismDisplay(vocabulary, code, resolution, locale) {
    const stored = typeof code === 'string' ? code.trim() : '';
    if (stored.length === 0)
        return { state: 'empty' };
    if (resolution === 'needs_redetermination')
        return { state: 'needs_redetermination', code: stored };
    if (vocabulary === null)
        return { state: 'stored', code: stored };
    const found = findTransferMechanismOption(vocabulary.options, stored);
    if (found) {
        const label = prefersVietnamese(locale) ? found.option.label.vi : found.option.label.en;
        return {
            state: 'label',
            text: label.trim().length > 0 ? label : stored,
            citation: found.option.citation,
            declared_by: found.option.declared_by_framework_code,
            code: stored,
        };
    }
    if (transferMechanismVocabularyIsComplete(vocabulary)) {
        return { state: 'needs_redetermination', code: stored };
    }
    const unverified_instruments = transferMechanismUnverifiedInstruments(vocabulary);
    // OD43a — no declaring stack: nothing is in the measurement, so there is no
    // instrument to name and "not yet verified: " would name none.
    if (unverified_instruments.length === 0)
        return { state: 'stored', code: stored };
    return { state: 'unverified', code: stored, unverified_instruments };
}
/**
 * `recordedTransferMechanismDisplay` as one string, for a surface that holds
 * text only (an export column). Function replacements, so a stored code that
 * reads `$&` is quoted verbatim (the 62.27.0 P2-4 lesson).
 */
export function recordedTransferMechanismText(vocabulary, code, resolution, locale, sentences) {
    const display = recordedTransferMechanismDisplay(vocabulary, code, resolution, locale);
    switch (display.state) {
        case 'empty':
            return '';
        case 'label':
            return `${display.text} (${display.citation})`;
        case 'stored':
            return display.code;
        case 'needs_redetermination':
            return sentences.needsRedetermination.replace('{mechanism}', () => display.code);
        case 'unverified':
            return sentences.unverified
                .replace('{mechanism}', () => display.code)
                .replace('{instruments}', () => display.unverified_instruments.join(', '));
        default: {
            const unreachable = display;
            return unreachable;
        }
    }
}
/** Matches the language subtag, as `lawfulBasisLabel` does (`vi` and `vi-VN`). */
function prefersVietnamese(locale) {
    return locale.split('-')[0]?.toLowerCase() === 'vi';
}
//# sourceMappingURL=transfer-mechanism.js.map